import { ColumnLayout, FormField, Select, SpaceBetween, Textarea, Toggle } from '@amzn/awsui-components-core';
import Input from '@amzn/awsui-components-core/input';
import { FormikProps, getIn } from 'formik';
import React from 'react';
import { useAppContext } from 'src/components/context/AppContextProvider';
import {
  labelInputToLabelDropdownOptions,
  labelInputToSelectDropdown,
  selectDropdownToLabel
} from 'src/components/generic-components/LabelDropdownUtility';
import { characterCountConstraintMessage } from 'src/utilities/CommonUtilities';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';
import { ApplicationFormValues } from '../../AdminModels';
import { APP_NAME_MAX_LENGTH, FULL_DESCRIPTION_MAX_LENGTH, SHORT_DESCRIPTION_MAX_LENGTH } from './ApplicationFormValidations';
import { ApplicationTags } from './LinksAndTags';

interface ApplicationOverviewConfigProps {
  formik: FormikProps<ApplicationFormValues>;
}

const ApplicationOverviewConfig: React.FC<ApplicationOverviewConfigProps> = ({ formik }) => {
  const appContext = useAppContext();

  return (
    <SpaceBetween size="m" direction="vertical">
      <ColumnLayout columns={3}>
        <FormField
          label="Application Name"
          description="Enter the unique application name"
          errorText={getIn(formik.touched, 'applicationDetails.applicationName') && getIn(formik.errors, 'applicationDetails.applicationName')}
          constraintText={characterCountConstraintMessage(APP_NAME_MAX_LENGTH, formik.values.applicationDetails?.applicationName?.length || 0)}
          stretch={true}
        >
          <Input
            placeholder="Enter application name"
            onChange={({ detail }) => {
              formik.setFieldValue('applicationDetails.applicationName', detail.value);
            }}
            onBlur={() => {
              formik.setFieldTouched('applicationDetails.applicationName', true);
            }}
            value={formik.values.applicationDetails?.applicationName || ''}
          />
        </FormField>

        <FormField label="Flagship Product" description="Mark this app as a flagship product">
          <Toggle
            checked={formik.values.applicationDetails?.isFlagship || false}
            onChange={({ detail }) => {
              formik.setFieldValue('applicationDetails.isFlagship', detail.checked);
            }}
          >
            {formik.values.applicationDetails?.isFlagship ? 'Enabled' : 'Disabled'}
          </Toggle>
        </FormField>

        <FormField
          label="FinTech Team"
          description="Select the DaS FinTech team who owns this app"
          errorText={
            getIn(formik.touched, 'applicationDetails.applicationOwnerLabelInput') &&
            getIn(formik.errors, 'applicationDetails.applicationOwnerLabelInput.label')
          }
        >
          <Select
            placeholder="Choose team"
            selectedOption={labelInputToSelectDropdown(formik.values.applicationDetails?.applicationOwnerLabelInput)}
            onChange={({ detail }) => {
              formik.setFieldValue('applicationDetails.applicationOwnerLabelInput', selectDropdownToLabel(detail.selectedOption, 'ApplicationOwner'));
            }}
            onBlur={() => {
              formik.setFieldTouched('applicationDetails.applicationOwnerLabelInput', true);
            }}
            options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listApplicationOwners)}
            selectedAriaLabel="Selected"
          />
        </FormField>
      </ColumnLayout>

      <FormField
        label="Module"
        description="Select the category that best describes this app"
        errorText={getIn(formik.touched, 'applicationDetails.glAccounts') && getIn(formik.errors, 'applicationDetails.glAccounts.label')}
      >
        <Select
          placeholder="Choose category"
          selectedOption={labelInputToSelectDropdown(formik.values.applicationDetails?.glAccounts)}
          onChange={({ detail }) => {
            formik.setFieldValue('applicationDetails.glAccounts', selectDropdownToLabel(detail.selectedOption, 'GLAccount'));
          }}
          onBlur={() => {
            formik.setFieldTouched('applicationDetails.glAccounts', true);
          }}
          options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listGLAccounts)}
          filteringType="auto"
          selectedAriaLabel="Selected"
        />
      </FormField>

      <FormField
        label="Short Application Description"
        description={`Short descriptions will be displayed in the navigation bar and "${Titles.TITLE_ALL_APPS}" page`}
        errorText={
          getIn(formik.touched, 'applicationDetails.shortApplicationDescription') &&
          getIn(formik.errors, 'applicationDetails.shortApplicationDescription')
        }
        constraintText={characterCountConstraintMessage(
          SHORT_DESCRIPTION_MAX_LENGTH,
          formik.values.applicationDetails?.shortApplicationDescription?.length || 0
        )}
        stretch={true}
      >
        <Textarea
          placeholder="Enter the short description"
          onChange={({ detail }) => {
            formik.setFieldValue('applicationDetails.shortApplicationDescription', detail.value);
          }}
          onBlur={() => {
            formik.setFieldTouched('applicationDetails.shortApplicationDescription', true);
          }}
          value={formik.values.applicationDetails?.shortApplicationDescription || ''}
        />
      </FormField>

      <FormField
        label="Full Application Description"
        description="Full descriptions will be displayed on the app's landing page"
        errorText={
          getIn(formik.touched, 'applicationDetails.fullApplicationDescription') &&
          getIn(formik.errors, 'applicationDetails.fullApplicationDescription')
        }
        constraintText={characterCountConstraintMessage(
          FULL_DESCRIPTION_MAX_LENGTH,
          formik.values.applicationDetails?.fullApplicationDescription?.length || 0
        )}
        stretch={true}
      >
        <Textarea
          placeholder="Enter the full description"
          onChange={({ detail }) => {
            formik.setFieldValue('applicationDetails.fullApplicationDescription', detail.value);
          }}
          onBlur={() => {
            formik.setFieldTouched('applicationDetails.fullApplicationDescription', true);
          }}
          value={formik.values.applicationDetails?.fullApplicationDescription || ''}
        />
      </FormField>

      <ApplicationTags formik={formik} />
    </SpaceBetween>
  );
};

export default ApplicationOverviewConfig;
